import React, { useMemo, useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';

interface CountdownTimerProps {
  maxDuration: number;
  elapsedSeconds: number;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ maxDuration, elapsedSeconds }) => {
  // 始终显示正计时
  const displayTime = elapsedSeconds;
  
  // 计算进度百分比 (0-100)
  // 如果没有最大时长，进度为0
  const percentage = maxDuration > 0 
    ? Math.min(100, (elapsedSeconds / maxDuration) * 100) 
    : 0;

  const formatTime = (totalSeconds: number) => {
    const safeSeconds = Math.max(totalSeconds, 0);
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const status = useMemo(() => {
    if (maxDuration <= 0) return 'normal';
    // 进度颜色变化逻辑：随着时间推移，颜色变化
    // 0-50%: normal (紫色)
    // 50-70%: caution (黄色)
    // 70-90%: warning (橙色)
    // >90%: critical (红色)
    if (percentage >= 90) return 'critical';
    // if (percentage >= 70) return 'warning';
    // if (percentage >= 50) return 'caution';
    return 'normal';
  }, [percentage, maxDuration]);

  const colorMap = {
    normal: '#A855F7', // purple-500
    caution: '#EAB308', // yellow-500
    warning: '#F97316', // orange-500
    critical: '#EF4444', // red-500
  };

  const currentColor = colorMap[status];
  const controls = useAnimation();
  const [prevStatus, setPrevStatus] = useState(status);

  // 监听状态变化，触发晃动动画
  useEffect(() => {
    if (status !== prevStatus && maxDuration > 0) {
      controls.start({
        x: [0, -4, 4, -4, 4, 0],
        transition: { duration: 0.4 }
      });
    }
    setPrevStatus(status);
  }, [status, prevStatus, controls, maxDuration]);

  return (
    <motion.div 
      className={`relative w-20 h-6 rounded-full overflow-hidden flex items-center justify-center shadow-inner ${maxDuration > 0 ? 'bg-gray-100' : 'bg-white'}`}
      animate={controls}
    >
      {/* Background Progress Bar - Fills up */}
      {maxDuration > 0 && (
        <motion.div
          className="absolute left-0 top-0 bottom-0 h-full"
          initial={{ width: '0%' }}
          animate={{ 
            width: `${percentage}%`,
            backgroundColor: currentColor,
            opacity: status === 'critical' ? 0.3 : 0.2
          }}
          transition={{
            width: { duration: 0.5, ease: "linear" },
            backgroundColor: { duration: 0.3 }
          }}
        />
      )}
      
      {/* Active Progress Bar Line (Bottom) */}
      {maxDuration > 0 && false && (
        <motion.div
          className="absolute left-0 bottom-0 h-1"
          initial={{ width: '0%' }}
          animate={{ 
            width: `${percentage}%`,
            backgroundColor: currentColor
          }}
          transition={{ duration: 0.5, ease: "linear" }}
        />
      )}

      {/* Time Text */}
      <motion.span 
        className="relative z-10 text-sm font-semibold tabular-nums tracking-wide"
        animate={{ 
          color: status === 'critical' ? '#EF4444' : '#4B5563', // gray-600 normally
          scale: status === 'critical' ? [1, 1.1, 1] : 1
        }}
        transition={{
          scale: status === 'critical' ? { repeat: Infinity, duration: 1 } : {},
          color: { duration: 0.3 }
        }}
      >
        {formatTime(displayTime)}
      </motion.span>
    </motion.div>
  );
};

export default CountdownTimer;
